import React from "react";
import { Alert, Box } from "@mui/material";
import FileUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useReceiptUploadDialog } from "@features/bay-hire/hooks/useReceiptUploadDialog";
import {
  RECEIPT_UPLOAD_DIALOG_FIELDS as F,
  type ReceiptUploadDialogFormData,
} from "@features/bay-hire/schema/receiptUploadDialogSchema";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ReceiptUploadDialogProps {
  open: boolean;
  requestId: string;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

/**
 * DFT-0014 (F-04 wireframe, AD-19): Vendor payment-receipt upload panel.
 * Reuses the DFT-0013 PreApproveDialog's two-phase upload pattern.
 *
 * DFT-0027 (Slice C): shell converted from the centered MUI Dialog to the
 * shared right-anchored SideDrawer. External props, form logic, schema,
 * mutation hook, file-upload flow, and success/close behaviour are unchanged -
 * only the shell and footer buttons change. `open` maps onto SideDrawer's
 * `isOpen`; submit is wired via RHF handleSubmit.
 *
 * DFT-0050 (AC19, AC24): rebuilt on the shared `FileUpload` component - the
 * previous raw `<input type="file" hidden>` + trigger `Button` had no
 * `FileUpload` import and no remove affordance at all (confirmed via PR #104's
 * `marhub`-checkout verification), so a mis-selected or no-longer-wanted
 * receipt could not be removed once staged. Mirrors `ReasonDialog`'s own
 * remove-control fix: a `SingleFile` row renders once a file is staged,
 * wired to `handleRemoveReceipt`.
 */
const ReceiptUploadDialog: React.FC<ReceiptUploadDialogProps> = ({
  open,
  requestId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const uploadConditions = useFileUploadProfiles().bayHireAttachment;

  const {
    form,
    receiptFileName,
    submitError,
    isSubmitting,
    isReceiptStaging,
    files,
    setFiles,
    handleReceiptUpload,
    handleRemoveReceipt,
    handleSubmitForm,
    handleClose,
  } = useReceiptUploadDialog({ open, requestId, onClose, onSuccess });

  const { handleSubmit } = form;

  // Extracted with an explicit `value: unknown` parameter (matches
  // usePreApproveDialog/useBayHireRequestForm's handleDateFieldChange
  // pattern) - FormItem's inline onFieldChange infers `value` as
  // PathValue<T, K> (string for this field), which TS rejects as the LHS of
  // `instanceof` (TS2358). A function typed to accept the wider `unknown`
  // is still assignable to the narrower prop type via parameter contravariance.
  const handleReceiptDateChange = (_key: typeof F.RECEIPT_DATE, value: unknown) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      const yyyy = value.getFullYear();
      const mm = String(value.getMonth() + 1).padStart(2, "0");
      const dd = String(value.getDate()).padStart(2, "0");
      form.setValue(F.RECEIPT_DATE, `${yyyy}-${mm}-${dd}`, {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      form.setValue(F.RECEIPT_DATE, "", { shouldValidate: true, shouldDirty: true });
    }
  };

  return (
    <div data-testid="receipt-upload-dialog">
      <SideDrawer
        isOpen={open}
        title={t("bay-hire.receipt-upload.title")}
        onClose={handleClose}
        onSubmit={() => void handleSubmit(handleSubmitForm)()}
        isSubmitting={isSubmitting}
        submitLabel={t("bay-hire.receipt-upload.submit")}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
      >
        <div className="flex flex-col gap-1 px-6 py-2">
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }} data-testid="receipt-upload-submit-error">
              {submitError}
            </Alert>
          )}

          <FormProvider {...form}>
            <FormItem<ReceiptUploadDialogFormData, typeof F.RECEIPT_NUMBER>
              required
              name={F.RECEIPT_NUMBER}
              type="text"
              label={t("bay-hire.receipt-upload.receiptNumber")}
              testId="receipt-upload-receipt-number"
              // DFT-0081 (requester follow-up): the 10-character cap must be enforced live, not
              // only at submit - maxChars blocks further typing past the limit and surfaces
              // FormItem's own derived "max length" error immediately (same live-enforcement
              // mechanism RECEIPT_AMOUNT's maxDigits already gives the amount field above),
              // rather than relying solely on receiptUploadDialogSchema.ts's submit-time check.
              // maxCharsCustomError keeps the live message identical to the schema's own
              // over-limit message (FormItem's generic "form-validation.max-length" default
              // would otherwise read differently from the two).
              maxChars={10}
              maxCharsCustomError={t("bay-hire.receipt-upload.number-too-long")}
            />

            <FormItem<ReceiptUploadDialogFormData, typeof F.RECEIPT_DATE>
              required
              name={F.RECEIPT_DATE}
              type="date-picker"
              timePicker={false}
              label={t("bay-hire.receipt-upload.receiptDate")}
              testId="receipt-upload-receipt-date"
              onFieldChange={handleReceiptDateChange}
            />

            <FormItem<ReceiptUploadDialogFormData, typeof F.RECEIPT_AMOUNT>
              required
              name={F.RECEIPT_AMOUNT}
              type="number"
              label={t("bay-hire.receipt-upload.receiptAmount")}
              testId="receipt-upload-receipt-amount"
              // DFT-0081: receipt_amount is NUMERIC(12,2) on the domain side (10 integer
              // digits, 2 decimal places) - identical wiring to PreApproveDialog.tsx's
              // TOTAL_AMOUNT fix, same shared FormItem/NumberField mechanism.
              maxDigits={10}
              decimals={2}
              canBeginWithZero={false}
            />
          </FormProvider>

          <Box sx={{ mt: 2 }}>
            <FileUpload
              testId="receipt-upload-file"
              // DFT-0096: keyed by attachment type (mirrors
              // SingleCertificate.tsx's useUploadStore key convention) so a
              // staged-but-not-yet-submitted upload survives a refresh via
              // useUploadStore (FileUpload's own built-in integration - see
              // its `name` prop). handleReceiptUpload overwrites this key's
              // entry with the real {name, url} once the upload succeeds.
              name="PAYMENT_RECEIPT"
              onUpload={(selected) => void handleReceiptUpload(selected)}
              onRemoveFile={handleRemoveReceipt}
              files={files}
              setFiles={setFiles}
              maxFiles={1}
              allowedExtensions={(uploadConditions.format ?? []).map((ext) => `.${ext}`)}
              conditions={uploadConditions}
              disabled={isSubmitting || isReceiptStaging}
              // DFT-0088 addendum (2026-09-01): matches the actual `bayHireAttachment`
              // allow-list - see FileUpload.constants.ts's own comment on that profile.
              uploadDescription={t("upload.dragOrDropDescription")}
            />

            {/* AC19/AC21 (DFT-0050): a visible remove control for the staged receipt. */}
            {receiptFileName && (
              <div className="mt-1" data-testid="receipt-upload-file-staged">
                <SingleFile
                  testId="receipt-upload-attachment"
                  file={files[0] ?? { name: receiptFileName }}
                  fieldName="receipt-upload-attachment"
                  setFiles={() => setFiles([])}
                  removeFn={handleRemoveReceipt}
                  disabled={isSubmitting || isReceiptStaging}
                />
              </div>
            )}
          </Box>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ReceiptUploadDialog;
